/***
 * Created on 2024/8/29
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useEffect, useMemo, useState} from 'react';
import {QueryClient, QueryClientProvider, useQuery} from "@tanstack/react-query";
import sharedI18n from "common/SharedI18n";
import {I18nextProvider, useTranslation} from "react-i18next";
import TextInput from "../common/form/text_input";
import {useOnChange, useLanguage} from "common/Hook";
import PasswordInput from "../common/form/password_input";
import GdprDialog from "./gdpr_dialog";
import {getErrorMessage, getCurrentDomainRegion, convertToQNAPLang, getPrivacyPolicyUrl, URLHandler} from 'common/ServiceCommon';
import Captcha from "../common/captcha";
import {apiGet} from "../../service/api";


const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // default: true
        },
    },
});

function InviteSignup({params}) {
    const { t, i18n } = useTranslation();
    useLanguage(i18n);
    const [showGDPR, setShowGDPR] = useState(false);
    const [disclaimer, setDisclaimer] = useState(false);
    const [passwordMatch, setPasswordMatch] = useState(false);
    const {values, setValues, onChange} = useOnChange({
        qid: '',
        first_name: '',
        last_name: '',
        display_name: '',
        password: '',
        confirm_password: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        captcha: ''
    });

    const isValid = useMemo(()=> {
        return disclaimer && values?.qid && values.password && passwordMatch;
    }, [values.qid, values.password, values.confirm_password, disclaimer, passwordMatch]);

    const {data, isError, isSuccess} = useQuery({
        queryKey: ['get_invitation'],
        queryFn: async () => await apiGet(`/invitation/${params?.invite_id}`),
    });

    if (isError) {
        location.href = '/error?code=1007001';
    }

    useEffect(() => {
        if (data) {
            setValues(prev => {
                return {...prev, qid: data.qid};
            });
        }
    }, [data, isSuccess]);

    return (
        <section className="common-page-wrapper common-page-wrapper--sign-up" data-show-sns="false">
            <div className="common-page-header">{t('@Create Account@')}</div>
            <div className="common-page-body">
                <div className="sign-up--regular">
                    <div className="sign-up--regular__subtitle display-flex align-item-center">
                        <div className="comp-tag mr-8">
                            <div className="q-tag q-tag--tiny q-tag--default">
                                <span className="tag__label">QID</span>
                            </div>
                        </div>
                        <div className="flex-1 text-ellipsis">{values.qid}</div>
                    </div>

                    <div className="sign-up--regular__group">
                        <div className="common-form-group">

                            <div className="comp-input !mb-24">
                                <TextInput label={t('@Last Name@')} placeholder={t('@Last Name@')}
                                           name='last_name' value={values.last_name} onChange={onChange}/>
                            </div>

                            <div className="comp-input !mb-24">
                                <TextInput label={t('@First Name@')} placeholder={t('@First Name@')}
                                           name='first_name' value={values.first_name} onChange={onChange}/>
                            </div>

                            <div className="comp-input !mb-24">
                                <TextInput label={t('@Display Name@')} placeholder={t('@Display Name@')}
                                           name='display_name' value={values.display_name} onChange={onChange}/>
                            </div>

                            <PasswordInput
                                label={t('@Password@')}
                                placeholder={t('@Password@')}
                                name='password'
                                value={values.password}
                                onChange={onChange}
                                required={true}
                                showStrength={true}
                                ignoreRules={false}
                                showTip={true}/>

                            <PasswordInput
                                label={t('@Confirm Password@')}
                                placeholder={t('@Confirm Password@')}
                                name='confirm_password'
                                value={values.confirm_password}
                                onChange={onChange}
                                status={setPasswordMatch}
                                matchInput={values.password}
                                required={true} />

                            <Captcha value={values.captcha} inputChangeHandler={onChange} resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>

                        </div>
                    </div>

                    <div className="common-form-group">
                        <div className="comp-checkbox">
                            <label className="q-checkbox">
                                <input type="checkbox" name="checkbox-terms" checked={disclaimer}
                                       onChange={() => setDisclaimer(prev => !prev)}/>
                                <span dangerouslySetInnerHTML={
                                    {
                                        __html: t('@I agree to myQNAPcloud Terms of Service and Privacy Policy.@').replace(`<a ng-click='showTermsOfUse()'>`, `<a href="${window.acc_mf.common.APP_QNAP_URL}/${convertToQNAPLang(i18n.language)}/legal/qnap-cloud-service-terms-and-conditions" target="_blank">`).replace(`<a ng-click='showPrivacyPolicy()'>`, `<a href="${getPrivacyPolicyUrl()}" target="_blank">`)
                                    }
                                }></span>
                            </label>
                        </div>
                    </div>

                    <button className="q-btn q-btn--primary--focus btn-medium btn-rounded-normal" disabled={!isValid}
                            onClick={() => setShowGDPR(true)}>{t('@Sign Up@')}</button>
                </div>
            </div>
            {showGDPR && <GdprDialog showModal={showGDPR} closeModal={() => setShowGDPR(false)} data={values} invite_id={params?.invite_id}/>}
        </section>
    );
}

function InviteSignupIndex({params}) {
    return (
        <I18nextProvider i18n={sharedI18n}>
            <QueryClientProvider client={queryClient}>
                <InviteSignup params={params} />
            </QueryClientProvider>
        </I18nextProvider>
    );
}

export default InviteSignupIndex;